import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { DAYS_META } from '../data/questionsData';
import { quizService, getCurrentUserId } from '../services/quizService';
import { mapApiDayStatusToDayStatus } from '../services/assessmentApi';
import { ApiDayItem, DayStatus } from '../data/types';
import { QuizLoading } from './QuizLoading';
import { MentorAssessmentDashboard, isUserAdmin } from './MentorAssessmentDashboard';

interface QuizDashboardProps {
  studentName?: string;
  onSelectDay: (day: number) => void;
  apiDays?: ApiDayItem[];
  isLoading?: boolean;
}

const DAYPILL: Record<DayStatus, { label: string; fg: string; bg: string }> = {
  COMPLETED: { label: 'Đã hoàn thành', fg: '#0B6F79', bg: '#E1F3F4' },
  IN_PROGRESS: { label: 'Đang thực hiện', fg: '#8A6400', bg: '#FBF1D6' },
  AVAILABLE: { label: 'Chưa bắt đầu', fg: '#15243C', bg: '#E8ECF2' },
  LOCKED: { label: 'Đã khóa', fg: '#8A94A3', bg: '#EEF1F4' },
};

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDT = (iso: string | null) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

// Short words that stay upper case when an ALL-CAPS title from the API is turned into sentence case
const KEEP_UPPER = new Set(['FOMO', 'SL', 'TP', 'RR', 'R:R', 'EA', 'FTMO']);

/** "Ngày 4 - TÍNH CHỌN LỌC" -> "Tính chọn lọc", so every card title reads the same way */
const cleanDayTitle = (raw: string | undefined, fallback: string) => {
  const title = (raw || '').replace(/^Ngày\s*\d+\s*[-–—:]\s*/i, '').trim();
  if (!title) return fallback;
  const hasLower = title !== title.toLocaleUpperCase('vi');
  if (hasLower) return title;
  const lower = title
    .split(/(\s+)/)
    .map((w) => (KEEP_UPPER.has(w) ? w : w.toLocaleLowerCase('vi')))
    .join('');
  return lower.charAt(0).toLocaleUpperCase('vi') + lower.slice(1);
};

interface DayCardData {
  day: number;
  title: string;
  count: number;
  answered: number;
  status: DayStatus;
  locked: boolean;
  dateLabel: string;
  actionLabel: string;
}

function buildDayCard(dayNumber: number, apiItem: ApiDayItem | undefined): DayCardData {
  const meta = DAYS_META.find((m) => m.day === dayNumber);
  const session = quizService.getDaySession(dayNumber);
  const count = apiItem?.total_questions || meta?.totalQuestions || meta?.count || 34;

  // Status: the API wins over the local session
  const status: DayStatus = apiItem?.status ? mapApiDayStatusToDayStatus(apiItem.status) : session.status;

  // Answered: the API's "X / Y" progress first, then answers locked locally, then the local draft
  let answered: number | null = null;
  if (apiItem?.progress) {
    const parsed = parseInt(apiItem.progress.split('/')[0].trim(), 10);
    if (!isNaN(parsed)) answered = parsed;
  }
  if (answered === null) {
    try {
      const raw = localStorage.getItem(`fc_quiz_locked_u_${getCurrentUserId()}_day_${dayNumber}`);
      const lockedIds = raw ? JSON.parse(raw) : null;
      if (Array.isArray(lockedIds)) answered = lockedIds.length;
    } catch {}
  }
  if (answered === null) {
    answered = session.answers ? Object.keys(session.answers).length : 0;
  }
  answered = Math.min(answered, count);

  const dateLabel = session.submittedAt
    ? `Nộp ${fmtDT(session.submittedAt)}`
    : session.startedAt && status !== 'AVAILABLE'
    ? `Bắt đầu ${fmtDT(session.startedAt)}`
    : '';

  let actionLabel = 'Chưa mở';
  if (status === 'COMPLETED') actionLabel = 'Xem lại câu trả lời';
  else if (status === 'IN_PROGRESS') actionLabel = `Tiếp tục Ngày ${dayNumber}`;
  else if (status === 'AVAILABLE') actionLabel = `Bắt đầu Ngày ${dayNumber}`;

  return {
    day: dayNumber,
    title: cleanDayTitle(apiItem?.title, meta?.title || `Ngày ${dayNumber}`),
    count,
    answered,
    status,
    locked: status === 'LOCKED',
    dateLabel,
    actionLabel,
  };
}

export const QuizDashboard: React.FC<QuizDashboardProps> = ({
  studentName = 'Học viên',
  onSelectDay,
  apiDays,
  isLoading = false,
}) => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const unsubscribe = quizService.subscribe(() => {
      setTick((t) => t + 1);
    });
    return unsubscribe;
  }, []);

  // Day cards from the API, or from the local metadata until the API answers
  const daysData = useMemo(() => {
    if (Array.isArray(apiDays) && apiDays.length > 0) {
      return apiDays.map((apiItem) => buildDayCard(Number(apiItem.day_number || apiItem.id), apiItem));
    }
    return DAYS_META.map((meta) => buildDayCard(meta.day, undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, apiDays]);

  const totalAnswered = daysData.reduce((acc, d) => acc + d.answered, 0);
  const totalQuestions = daysData.reduce((acc, d) => acc + d.count, 0) || 100;
  const completedDays = daysData.filter((d) => d.status === 'COMPLETED').length;
  const totalProgressW = `${Math.min(100, (totalAnswered / totalQuestions) * 100).toFixed(1)}%`;

  // The day to work on next: the first one that is not finished and not locked
  const nextDay = useMemo(
    () => daysData.find((d) => d.status !== 'COMPLETED' && !d.locked) || null,
    [daysData]
  );

  // Overall status follows the day statuses, not only the answer count:
  // a started day with no answer yet is still "in progress"
  const overallStatus = useMemo(() => {
    if (daysData.length > 0 && completedDays === daysData.length) return DAYPILL.COMPLETED;
    const started = daysData.some((d) => d.status === 'IN_PROGRESS' || d.status === 'COMPLETED' || d.answered > 0);
    return started ? DAYPILL.IN_PROGRESS : DAYPILL.AVAILABLE;
  }, [daysData, completedDays]);

  const { ctaLabel, ctaTargetDay } = useMemo(() => {
    if (!nextDay) {
      return { ctaLabel: 'Xem lại kết quả', ctaTargetDay: daysData[0]?.day || 1 };
    }
    const isFirstStart = nextDay.status === 'AVAILABLE' && overallStatus === DAYPILL.AVAILABLE;
    return {
      ctaLabel: isFirstStart
        ? 'Bắt đầu bài đánh giá'
        : nextDay.status === 'IN_PROGRESS'
        ? `Tiếp tục Ngày ${nextDay.day}`
        : `Bắt đầu Ngày ${nextDay.day}`,
      ctaTargetDay: nextDay.day,
    };
  }, [nextDay, daysData, overallStatus]);

  if (isLoading) {
    return <QuizLoading type="dashboard" message="Đang tải dữ liệu các ngày đánh giá..." />;
  }

  return (
    <div className="fc-dashboard-container">

      {/* Top Header */}
      <div className="fc-dash-header">
        <div>
          <div className="fc-dash-greeting">Xin chào, {studentName}</div>
          <h1 className="fc-dash-title">Đánh giá Năng lực</h1>
        </div>
        <span
          className="fc-dash-status-pill"
          style={{ background: overallStatus.bg, color: overallStatus.fg }}
        >
          {overallStatus.label}
        </span>
      </div>

      {/* Hero Progress Banner */}
      <div className="fc-dash-hero">
        <div className="fc-dash-hero-info">
          <div className="fc-dash-hero-label">TIẾN ĐỘ</div>
          <div className="fc-dash-hero-count">
            <span className="fc-dash-hero-num">{totalAnswered}</span>
            <span className="fc-dash-hero-total">/ {totalQuestions} câu</span>
            <span className="fc-dash-hero-days">
              {completedDays}/{daysData.length} ngày hoàn thành
            </span>
          </div>
          <div className="fc-dash-hero-track">
            <div className="fc-dash-hero-fill" style={{ width: totalProgressW }} />
          </div>
          {nextDay && (
            <div className="fc-dash-hero-next">
              Tiếp theo: <b>Ngày {nextDay.day} · {nextDay.title}</b>
            </div>
          )}
        </div>
        <button
          type="button"
          className="fc-dash-cta-btn"
          onClick={() => onSelectDay(ctaTargetDay)}
        >
          {ctaLabel}
        </button>
      </div>

      {/* Day cards: the whole card opens the day */}
      <div className="fc-dash-grid">
        {daysData.map((d) => {
          const pill = DAYPILL[d.status] || DAYPILL.AVAILABLE;
          const pct = d.count > 0 ? Math.min(100, (d.answered / d.count) * 100) : 0;
          const isNext = nextDay?.day === d.day;
          return (
            <button
              key={d.day}
              type="button"
              className={`fc-dash-day-card is-${d.status.toLowerCase()}${isNext ? ' is-next' : ''}`}
              onClick={() => onSelectDay(d.day)}
              disabled={d.locked}
              aria-label={`Ngày ${d.day}: ${d.title} — ${d.actionLabel}`}
            >
              <div className="fc-dash-day-top">
                <div className="fc-dash-day-heading">
                  <div className="fc-dash-day-name">
                    Ngày {d.day}
                    {isNext && <span className="fc-dash-day-next">Tiếp theo</span>}
                  </div>
                  <div className="fc-dash-day-sub">
                    {d.title} · {d.count} câu
                  </div>
                </div>
                <span className="fc-dash-day-pill" style={{ background: pill.bg, color: pill.fg }}>
                  {pill.label}
                </span>
              </div>

              <div className="fc-dash-day-progress">
                <div className="fc-dash-day-track">
                  <div className="fc-dash-day-fill" style={{ width: `${pct.toFixed(1)}%` }} />
                </div>
                <div className="fc-dash-day-meta">
                  <span className="mono-font">
                    {d.answered} / {d.count}
                  </span>
                  <span>{d.dateLabel}</span>
                </div>
              </div>

              <span className="fc-dash-day-action-btn">
                <span>{d.actionLabel}</span>
                <ChevronRight size={15} />
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="fc-dash-footer-note">
        Mỗi ngày là một phiên riêng. Câu trả lời được tự động lưu; sau khi hoàn thành một ngày, câu trả lời của ngày đó được khóa và ngày tiếp theo được mở.
      </div>

      {/* Mentor & Admin Assessment Dashboard (Chỉ hiện khi user có role admin hoặc super_admin) */}
      {isUserAdmin() && <MentorAssessmentDashboard apiDays={apiDays} />}
    </div>
  );
};

export default QuizDashboard;
